import { forwardRef, useState, useRef } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { useTranslation } from 'react-i18next';
import { useRegisterWaste } from '../../hooks';
import { useToast } from '@shared/hooks/use-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUpload } from '@fortawesome/pro-light-svg-icons';

interface RegisterWasteModalProps {
  orderId: number;
  lineId: number;
  onSuccess?: () => void;
  qty?: number; // Cantidad predefinida (opcional, si viene, se oculta el campo)
}

/**
 * Modal para registrar merma en una línea de trabajo
 */
export const RegisterWasteModal = forwardRef<ModalCenterRef, RegisterWasteModalProps>(
  ({ orderId, lineId, onSuccess, qty: initialQty }, ref) => {
    const { t } = useTranslation('extraWorkOrdersModule');
    const { toast } = useToast();
    const registerWasteMutation = useRegisterWaste();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Estado del formulario
    const [qty, setQty] = useState<string>(initialQty ? String(initialQty) : '');
    const [description, setDescription] = useState<string>('');
    const [evidences, setEvidences] = useState<File[]>([]);

    // Resetear formulario
    const resetForm = () => {
      setQty(initialQty ? String(initialQty) : '');
      setDescription('');
      setEvidences([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    // Handlers
    const handleCancel = () => {
      (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
      resetForm();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      setEvidences([...evidences, ...files]);
    };

    const handleRemoveEvidence = (index: number) => {
      setEvidences(evidences.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
      // Validaciones
      const quantity = initialQty || Number(qty);
      console.log('🔍 RegisterWasteModal - Iniciando submit:', {
        initialQty,
        qty,
        quantity,
        evidencesCount: evidences.length,
      });

      if (!quantity || quantity <= 0) {
        console.error('❌ RegisterWasteModal - Cantidad inválida:', quantity);
        toast({
          title: t('modals.registerWaste.error.title', 'Error'),
          description: t('modals.registerWaste.error.invalid_qty', 'La cantidad debe ser mayor a 0'),
          variant: 'destructive',
        });
        return;
      }

      if (evidences.length === 0) {
        console.error('❌ RegisterWasteModal - No hay evidencias');
        toast({
          title: t('modals.registerWaste.error.title', 'Error'),
          description: t('modals.registerWaste.error.no_evidences', 'Se requiere al menos una evidencia'),
          variant: 'destructive',
        });
        return;
      }

      try {
        console.log('🔍 RegisterWasteModal - Registrando merma:', {
          orderId,
          lineId,
          qty: quantity,
          description,
          evidencesCount: evidences.length,
        });

        await registerWasteMutation.mutateAsync({
          orderId,
          lineId,
          data: {
            qty: quantity,
            description: description || null,
            evidences,
          },
        });

        console.log('✅ RegisterWasteModal - Merma registrada exitosamente');
        handleCancel();
        onSuccess?.();
      } catch (error) {
        console.error('❌ RegisterWasteModal - Error al registrar merma:', error);
        // El error ya se maneja en el hook
      }
    };

    return (
      <ModalCenter ref={ref} title={t('modals.registerWaste.title', 'Registrar Merma')} width="lg">
        <div className="space-y-6">
          {/* Cantidad - Solo mostrar si no viene predefinida */}
          {!initialQty && (
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                {t('modals.registerWaste.qty', 'Cantidad')} *
              </label>
              <Input
                type="number"
                min="1"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder={t('modals.registerWaste.qty', 'Cantidad')}
                data-testid="waste-qty-input"
              />
            </div>
          )}

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-text mb-2">
              {t('modals.registerWaste.description', 'Descripción (opcional)')}
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('modals.registerWaste.description', 'Descripción (opcional)')}
              data-testid="waste-description-input"
            />
          </div>

          {/* Evidencias */}
          <div>
            <label className="block text-sm font-medium text-text mb-2">
              {t('modals.registerWaste.evidences', 'Evidencias')} *
            </label>
            <p className="text-sm text-subtext mb-2">
              {t('modals.registerWaste.evidences_help', 'Se requiere al menos una evidencia (imagen/archivo)')}
            </p>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="hidden"
              data-testid="evidence-file-input"
            />

            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="add-evidence-button"
            >
              <FontAwesomeIcon icon={faUpload} className="w-3.5 h-3.5 mr-2" />
              {t('modals.registerWaste.add_evidence', 'Añadir evidencia')}
            </Button>

            {/* Lista de evidencias */}
            {evidences.length > 0 && (
              <div className="mt-4 space-y-2">
                {evidences.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-card-bg border border-card-border rounded"
                  >
                    <span className="text-sm text-text truncate flex-1">{file.name}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => handleRemoveEvidence(index)}
                      className="h-8 w-8 ml-2"
                      data-testid={`remove-evidence-${index}`}
                    >
                      <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Acciones */}
          <div className="flex justify-end gap-2 pt-4 border-t border-lines">
            <Button variant="outline" onClick={handleCancel} data-testid="cancel-button">
              {t('modals.registerWaste.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleSubmit} disabled={registerWasteMutation.isPending} data-testid="submit-button">
              {registerWasteMutation.isPending
                ? t('modals.registerWaste.submitting', 'Registrando...')
                : t('modals.registerWaste.submit', 'Registrar merma')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

RegisterWasteModal.displayName = 'RegisterWasteModal';
